import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { DataRequest, SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

type Props<T> = {
  field: string;
  entity: Entities;
  value: string;
  startWithWildcard?: boolean;
  data?: T;
  searchWithEmptyTerm?: boolean;
};

export function useEntitySuggestions<T>({
  field,
  entity,
  value,
  startWithWildcard = false,
  data,
  searchWithEmptyTerm = false,
}: Props<T>) {
  const requestData = useMemo<DataRequest>(() => {
    return {
      filter: [
        {
          [field]: { $eq: startWithWildcard ? '%' + value + '%' : value + '%' },
        },
        { [field]: { $ne: '' } },
        { [field]: { $ne: ' ' } },
      ],
      entity: entity,
      data: { id: null, [field]: null, ...data },
      sort: [{ field, direction: SortDirection.ASCENDING }],
    };
  }, [entity, field, value, startWithWildcard, data]);

  const response = useDataProvider<T>(
    requestData,
    !searchWithEmptyTerm && (!value || value?.trim().length === 0)
  );

  const suggestions = useMemo(() => {
    const list = response.data.map((obj) => ({
      value: obj[field] as string,
      obj,
    }));
    const uniqueByValue = [
      ...new Map(list.map((item) => [item.value, item])).values(),
    ];

    return uniqueByValue;
  }, [field, response.data]);

  return suggestions;
}
